import React from 'react'
import { apiCall } from './api'

/**
 * 🔐 URL VALIDATION UTILITY FOR REACT APPS
 * Used by: carone-admin and carone-front
 * Fixes Issues #74-79: Open redirects, javascript: URLs, target=_blank exposure
 */

/**
 * Allowed internal routes in React app
 */
const SAFE_ROUTES = [
    '/',
    '/home',
    '/dashboard',
    '/product',
    '/products',
    '/auction',
    '/auctions',
    '/user',
    '/users',
    '/profile',
    '/account',
    '/settings',
    '/cart',
    '/checkout',
    '/payment',
    '/invoice',
    '/order',
    '/orders',
    '/bid',
    '/bids',
    '/category',
    '/categories',
    '/search',
    '/login',
    '/logout',
    '/register',
    '/forgot-password',
    '/reset-password',
]

/**
 * Whitelisted external domains for external link protection (Issue #78)
 */
const WHITELISTED_DOMAINS = [
    'carone.com.uy',
    'oportunidades.carone.com.uy',
    'fundacioncarone.com.uy',
    // App's own domains (from .env.production/.staging/.development) - the
    // list above only covers the separate marketing site, so without these
    // any link between the admin app and its own API/frontend was being
    // rejected by isSafeUrl as "non-whitelisted".
    'ecommerce.auction',
    'localhost',
    'carone.local',
]

/**
 * Dangerous URL protocols to block
 */
const DANGEROUS_PROTOCOLS = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
]

/**
 * Resolve the customer-facing frontend's base URL.
 * Prefers `process.env.REACT_APP_FRONTEND_URL`. If missing or undefined in a deployment,
 * falls back by deriving the frontend origin from `global.site_url` or `window.location.origin`.
 */
export const getFrontendUrl = () => {
    // Read `process.env.REACT_APP_FRONTEND_URL` as a single expression, without a
    // `typeof process` guard: Vite's `define` statically replaces `process.env`
    // with a literal at build time, but leaves `typeof process` alone - and in the
    // browser `process` doesn't exist, so a guard here always skipped the
    // configured URL and the switch-user eye icon silently did nothing.
    let configured
    try {
        configured = process.env.REACT_APP_FRONTEND_URL
    } catch {}
    if (
        configured &&
        typeof configured === 'string' &&
        configured.trim() &&
        configured !== 'undefined'
    ) {
        return configured.trim().replace(/\/+$/, '')
    }

    const apiDomain =
        (typeof window !== 'undefined' &&
            (window.site_url || (window.global && window.global.site_url))) ||
        ''
    if (apiDomain) {
        try {
            const u = new URL(apiDomain)
            if (u.hostname.includes('-api-')) {
                u.hostname = u.hostname.replace('-api-', '-')
                return u.origin
            }
            if (u.hostname.startsWith('api.')) {
                u.hostname = u.hostname.replace(/^api\./, '')
                return u.origin
            }
            if (u.hostname.includes('api-')) {
                u.hostname = u.hostname.replace('api-', '')
                return u.origin
            }
        } catch {}
    }

    if (typeof window !== 'undefined' && window.location) {
        try {
            const u = new URL(window.location.origin)
            if (u.hostname.includes('-admin-')) {
                u.hostname = u.hostname.replace('-admin-', '-')
                return u.origin
            }
            if (u.hostname.startsWith('admin.')) {
                u.hostname = u.hostname.replace(/^admin\./, '')
                return u.origin
            }
            if (u.hostname.includes('admin-')) {
                u.hostname = u.hostname.replace('admin-', '')
                return u.origin
            }
            if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
                if (u.port === '4001') {
                    u.port = '4000'
                    return u.origin
                }
                if (u.port === '3001') {
                    u.port = '3000'
                    return u.origin
                }
            }
        } catch {}
    }

    return ''
}

/**
 * Check if URL is safe for navigation
 * @param {String} url - URL to validate
 * @returns {Boolean} - True if safe
 */
export const isSafeUrl = (url) => {
    if (!url || typeof url !== 'string') {
        return false
    }

    // Browsers strip tab/newline/CR and normalize backslashes to slashes
    // *before* resolving a URL's protocol/host, so string-prefix checks like
    // `startsWith('/')` or `startsWith('javascript:')` can be bypassed with
    // inputs such as "java\tscript:alert(1)" or "/\\evil.com". Resolving
    // through the real URL parser and comparing origins catches both.
    const stripped = url.replace(/[\t\n\r]/g, '')
    const trimmed = stripped.trim()

    if (!trimmed) {
        return false
    }

    const currentOrigin =
        typeof window !== 'undefined' && window.location
            ? window.location.origin
            : null

    let resolved
    try {
        resolved = new URL(trimmed, currentOrigin || 'http://internal.invalid')
    } catch {
        console.warn('[SECURITY] Invalid URL blocked')
        return false
    }

    const protocol = resolved.protocol.toLowerCase()
    if (protocol !== 'http:' && protocol !== 'https:') {
        console.warn('[SECURITY] Blocked dangerous URL')
        return false
    }

    // Same-origin (internal route) navigation is always allowed.
    if (currentOrigin && resolved.origin === currentOrigin) {
        return true
    }

    // Allow whitelisted external domains (Issue #78)
    const hostname = resolved.hostname.toLowerCase()
    for (const domain of WHITELISTED_DOMAINS) {
        if (hostname === domain || hostname.endsWith('.' + domain)) {
            return true
        }
    }

    // Allow the customer frontend domain configured or resolved for this deployment
    try {
        const feUrl = getFrontendUrl()
        if (feUrl) {
            const feHost = new URL(feUrl).hostname.toLowerCase()
            if (hostname === feHost || hostname.endsWith('.' + feHost)) {
                return true
            }
        }
    } catch {}

    console.warn('[SECURITY] Blocked non-whitelisted external domain')
    return false
}

/**
 * Safe navigation helper for React Router
 * @param {Function} navigate - React Router navigate function
 * @param {String} url - URL to navigate to
 */
export const safeNavigate = (navigate, url) => {
    if (isSafeUrl(url)) {
        navigate(url)
    } else {
        console.warn('[SECURITY] Navigation blocked for unsafe URL')
        navigate('/')
    }
}

/**
 * Safe window.location assignment
 * @param {String} url - URL to navigate to
 */
export const safeWindowLocationAssign = (url) => {
    if (isSafeUrl(url)) {
        window.location.assign(url)
    } else {
        console.warn('[SECURITY] window.location assignment blocked')
        window.location.assign('/')
    }
}

export const safeOpenInNewTab = (url) => {
    if (isSafeUrl(url)) {
        return window.open(url, '_blank', 'noopener,noreferrer')
    }

    console.warn('[SECURITY] window.open blocked')
    return null
}

const buildSecureSwitchUrl = (baseUrl, switchToken) =>
    `${baseUrl}/switch#${encodeURIComponent(switchToken)}`

const buildLegacySwitchUrl = (baseUrl, userId) =>
    `${baseUrl}/switch?user_id=${encodeURIComponent(btoa(String(userId)))}`

/**
 * Open the customer-facing frontend's "view as this user" tab.
 *
 * `secure` false: previous flow - `/switch?user_id=<base64 id>` (no token).
 * `secure` true: ask `/api/admin/issueSwitchToken` for a short-lived token and
 * open `/switch#<token>` (fragment is never sent to a server).
 *
 * The tab is opened synchronously during the click so popup blockers allow it,
 * then navigated once the URL is known.
 *
 * @param {String|Number} userId - target user's numeric id
 * @param {Boolean} [secure] - true when the secure switch flow is enabled
 * @returns {Promise<Boolean>} true if the tab was opened
 */
export const openSwitchUserTab = async (userId, secure = false) => {
    const baseUrl = getFrontendUrl()
    if (!baseUrl || !isSafeUrl(`${baseUrl}/switch`)) {
        console.warn(
            '[SECURITY] Frontend URL could not be resolved for switch user'
        )
        return false
    }

    const opened = window.open('about:blank', '_blank')
    if (!opened) {
        console.warn('Switch-user tab was blocked')
        return false
    }
    opened.opener = null

    if (!secure) {
        opened.location.replace(buildLegacySwitchUrl(baseUrl, userId))
        return true
    }

    try {
        const res = await apiCall('post', 'issueSwitchToken', {
            user_id: userId,
        })
        const switchToken = res?.data?.data?.responseData?.switchToken
        if (res?.data?.status !== 'success' || !switchToken) {
            opened.close()
            console.warn('[SECURITY] Unable to issue switch-user token')
            return false
        }
        opened.location.replace(buildSecureSwitchUrl(baseUrl, switchToken))
        return true
    } catch {
        opened.close()
        console.warn('[SECURITY] Unable to issue switch-user token')
        return false
    }
}

/**
 * Get safe href for anchor tags
 * @param {String} href - href value
 * @returns {String} - Safe href value
 */
export const getSafeHref = (href) => {
    if (!href) return '#'
    return isSafeUrl(href) ? href : '#'
}

/**
 * Get safe anchor attributes
 * Prevents window.opener exposure when opening in new tab
 * @param {String} href - href value
 * @param {Boolean} openNewTab - Should open in new tab
 * @returns {Object} - Safe attributes { href, target, rel }
 */
export const getSafeAnchorAttributes = (href, openNewTab = false) => {
    const attributes = {
        href: getSafeHref(href),
    }

    if (openNewTab) {
        attributes.target = '_blank'
        // CRITICAL: Add rel=noopener to prevent window.opener access (Issue #79)
        attributes.rel = 'noopener noreferrer'
    }

    return attributes
}

/**
 * Safe external link component (with rel=noopener)
 * @param {String} href - Link URL
 * @param {String} label - Link text
 * @returns {JSX} - Safe anchor element
 */
export const SafeExternalLink = ({ href, label, className = '' }) => {
    const safe = isSafeUrl(href)
    if (!safe) {
        console.warn('[SECURITY] Blocked unsafe external link:', href)
        return <span className={className}>{label}</span>
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
        >
            {label}
        </a>
    )
}

/**
 * Validate redirect query parameter
 * @param {String} redirectParam - Redirect URL from query parameter
 * @param {String} defaultUrl - Default redirect URL
 * @returns {String} - Safe redirect URL
 */
export const getSafeRedirectUrl = (redirectParam, defaultUrl = '/') => {
    if (!redirectParam) return defaultUrl

    if (isSafeUrl(redirectParam)) {
        return redirectParam
    }

    console.warn('[SECURITY] Rejected redirect parameter:', redirectParam)
    return defaultUrl
}

/**
 * Check if string contains javascript: protocol
 * @param {String} str - String to check
 * @returns {Boolean}
 */
export const hasJavaScriptProtocol = (str) => {
    if (!str) return false
    return str
        .replace(/[\t\n\r]/g, '')
        .toLowerCase()
        .includes('javascript:')
}

/**
 * Sanitize object property that might contain a URL
 * @param {Object} obj - Object to sanitize
 * @param {String} key - Property key
 * @returns {String} - Safe value or '#'
 */
export const sanitizeUrlProperty = (obj, key) => {
    if (!obj || !obj[key]) return '#'
    return isSafeUrl(obj[key]) ? obj[key] : '#'
}

const INPUT_LENGTH_LIMIT = 1000
const URL_FIELD_PATTERN =
    /(url|uri|redirect|return|next|callback|continue|target|link|href)/i

const stripHtmlTags = (value) =>
    value
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .replace(/<[^>]*>/g, '')

export const sanitizeTextInput = (value) => {
    if (typeof value !== 'string') return value
    const withoutNullByte = value.split('\0').join('')
    const normalized = stripHtmlTags(withoutNullByte).trim()
    return normalized.length > INPUT_LENGTH_LIMIT
        ? normalized.slice(0, INPUT_LENGTH_LIMIT)
        : normalized
}

export const sanitizeSearchInput = (value) => sanitizeTextInput(value)

export const getSafeQueryParam = (searchString, key, defaultValue = '') => {
    if (!searchString || !key) return defaultValue
    const params = new URLSearchParams(searchString)
    return sanitizeTextInput(params.get(key) || defaultValue)
}

export const sanitizeInputPayload = (payload, fieldName = '') => {
    if (Array.isArray(payload)) {
        return payload.map((entry) => sanitizeInputPayload(entry, fieldName))
    }

    if (payload && typeof payload === 'object') {
        if (typeof FormData !== 'undefined' && payload instanceof FormData) {
            const sanitizedFormData = new FormData()
            payload.forEach((entryValue, entryKey) => {
                if (typeof entryValue === 'string') {
                    sanitizedFormData.append(
                        entryKey,
                        sanitizeTextInput(entryValue)
                    )
                } else {
                    sanitizedFormData.append(entryKey, entryValue)
                }
            })
            return sanitizedFormData
        }

        const sanitizedObject = {}
        Object.entries(payload).forEach(([key, value]) => {
            sanitizedObject[key] = sanitizeInputPayload(value, key)
        })
        return sanitizedObject
    }

    if (typeof payload !== 'string') {
        return payload
    }

    const sanitized = sanitizeTextInput(payload)
    if (URL_FIELD_PATTERN.test(fieldName)) {
        if (hasJavaScriptProtocol(sanitized)) return ''
        if (/redirect|return|next|callback|continue/i.test(fieldName)) {
            return getSafeRedirectUrl(sanitized)
        }
    }
    return sanitized
}
